import React, { useState, useCallback } from "react";
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  InputAdornment,
  Divider,
  Typography,
} from "@material-ui/core";
import CreditCardIcon from "@material-ui/icons/CreditCard";
import { KeyboardDatePicker } from "@material-ui/pickers";
import { useStyles } from "./ReceiptLookupPage.styles";
import { ACCEPTED_CREDIT_CARDS } from "../../constants";
import apiClient from "../../auth/apiClient";
import ReceiptLookupService from "../../services/ReceiptLookupService";
import useReceiptLookupForm from "./useReceiptLookupForm";
import useLprVisibility from "./useLprVisibility";

const CARD_TYPE_OPTIONS = [
  { value: ACCEPTED_CREDIT_CARDS.VISA, label: "Visa" },
  { value: ACCEPTED_CREDIT_CARDS.MASTERCARD, label: "Mastercard" },
  { value: ACCEPTED_CREDIT_CARDS.DISCOVER, label: "Discover" },
  { value: ACCEPTED_CREDIT_CARDS.AMEX, label: "Amex" },
  { value: ACCEPTED_CREDIT_CARDS.Other, label: "Other" },
];

const ReceiptLookupForm = ({ entityId, lprEnabled, isContextLoaded }) => {
  const classes = useStyles();
  const receiptLookupService = new ReceiptLookupService(apiClient);
  const {
    values,
    errors,
    isFormValid,
    handleBlur,
    handlePaymentDateChange,
    handleCardTypeChange,
    handleLast4Change,
    handleAmountChange,
    handleTicketIdChange,
    handleLicensePlateChange,
    buildPayload,
  } = useReceiptLookupForm();
  const { show: showLicensePlate } = useLprVisibility(lprEnabled, isContextLoaded);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = useCallback(async () => {
    if (!isFormValid || isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    try {
      await receiptLookupService.submitReceiptLookup(
        entityId,
        buildPayload(entityId)
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [isFormValid, isSubmitting, entityId, buildPayload, receiptLookupService]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <KeyboardDatePicker
          fullWidth
          id="receipt-payment-date"
          inputVariant="outlined"
          format="MM/DD/YYYY"
          label="Payment Date"
          value={values.paymentDate}
          onChange={handlePaymentDateChange}
          onBlur={() => handleBlur("paymentDate")}
          error={Boolean(errors.paymentDate)}
          helperText={errors.paymentDate}
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          id="receipt-ticket-id"
          variant="outlined"
          label="Ticket ID (optional)"
          value={values.ticketId}
          onChange={(e) => handleTicketIdChange(e.target.value)}
          onBlur={() => handleBlur("ticketId")}
          error={Boolean(errors.ticketId)}
          helperText={errors.ticketId}
          inputProps={{ maxLength: 12 }}
        />
      </Grid>

      {showLicensePlate && (
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="receipt-license-plate"
            variant="outlined"
            label="License Plate (optional)"
            value={values.licensePlate}
            onChange={(e) => handleLicensePlateChange(e.target.value)}
            onBlur={() => handleBlur("licensePlate")}
            error={Boolean(errors.licensePlate)}
            helperText={errors.licensePlate}
            inputProps={{ maxLength: 10 }}
          />
        </Grid>
      )}

      <Grid item xs={12}>
        <Divider />
      </Grid>

      <Grid item xs={12}>
        <div className={classes.cardSectionHeader}>
          <CreditCardIcon fontSize="small" />
          <Typography variant="subtitle2">Credit Card Information</Typography>
        </div>
      </Grid>

      <Grid item xs={12}>
        <FormControl
          fullWidth
          variant="outlined"
          error={Boolean(errors.cardType)}
        >
          <InputLabel id="receipt-card-type-label" htmlFor="receipt-card-type">
            Credit Card Type
          </InputLabel>
          <Select
            labelId="receipt-card-type-label"
            id="receipt-card-type"
            label="Credit Card Type"
            value={values.cardType || ""}
            onChange={(e) => handleCardTypeChange(e.target.value)}
            onBlur={() => handleBlur("cardType")}
          >
            {CARD_TYPE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          id="receipt-last4"
          variant="outlined"
          label="Credit Card Last 4 Digits"
          value={values.last4}
          onChange={(e) => handleLast4Change(e.target.value)}
          onBlur={() => handleBlur("last4")}
          error={Boolean(errors.last4)}
          helperText={errors.last4}
          inputProps={{ inputMode: "numeric", maxLength: 4 }}
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          id="receipt-amount"
          variant="outlined"
          label="Amount"
          value={values.amountDisplay}
          onChange={(e) => handleAmountChange(e.target.value)}
          onBlur={() => handleBlur("amount")}
          error={Boolean(errors.amount)}
          helperText={errors.amount}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">$</InputAdornment>
            ),
            inputProps: { inputMode: "numeric" },
          }}
        />
      </Grid>

      <Grid item xs={12}>
        <Button
          data-id="receipt-find-button"
          className={classes.findReceiptButton}
          variant="contained"
          color="primary"
          size="large"
          disabled={!isFormValid || isSubmitting}
          onClick={handleSubmit}
        >
          Find Receipt
        </Button>
      </Grid>
    </Grid>
  );
};

export default ReceiptLookupForm;
